/**
 * Sunset math for the canvassing stat bar.
 *
 * The binding constraint on a door-knocking shift is dark, not energy: at
 * 8:40pm a volunteer with fourteen doors left has a real decision to make
 * (skip to the dense block, or accept that six of them do not get knocked),
 * and no other number on the screen tells them that.
 *
 * This is deliberately pure arithmetic — no network call, no dependency.
 * A sunrise/sunset API would be one more thing to be offline for, and this
 * whole view is built to keep working in a dead zone. The algorithm is the
 * standard NOAA/"sunrise equation" low-precision solar position model, good
 * to well under a minute at mid latitudes, which is far tighter than the
 * question ("do I have an hour of light?") needs.
 *
 * Coordinates come from the stops themselves, so there is no geolocation
 * permission prompt in the middle of a walk. When the route has no usable
 * coordinates the caller gets `null` and drops the element entirely — a
 * guessed sunset is worse than no sunset, because it would be believed.
 */

const DEG = Math.PI / 180;

/** Julian date of the Unix epoch. */
const JD_UNIX_EPOCH = 2440587.5;
/** Julian date of J2000.0 (2000-01-01 12:00 TT). */
const JD_J2000 = 2451545.0;
/** Mean obliquity of the ecliptic, degrees. */
const OBLIQUITY = 23.4397;
/**
 * Solar altitude at "sunset": the upper limb touching the horizon, with
 * refraction. Civil twilight (-6°) buys roughly another half hour of usable
 * light, but knocking a stranger's door after the sun is down is a different
 * act than knocking in daylight, so the honest line to draw is the sun.
 */
const SUNSET_ALTITUDE = -0.833;

export interface Coord {
  lat: number;
  lng: number;
}

/**
 * Mean of the usable stop coordinates.
 *
 * A route spans a few blocks, so any interior point is equivalent for solar
 * purposes — a mile of longitude moves sunset by about four seconds. The mean
 * is used only because it needs no sorting.
 *
 * `(0, 0)` is rejected the same way the map surface rejects it: it is the
 * geocoder's sentinel for "this address did not resolve", and averaging it in
 * would drag the centroid into the Atlantic.
 */
export function meanCoord(points: Array<{ lat?: number | null; lng?: number | null }>): Coord | null {
  let latSum = 0;
  let lngSum = 0;
  let n = 0;
  for (const p of points) {
    const { lat, lng } = p;
    if (typeof lat !== 'number' || typeof lng !== 'number') continue;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    if (lat === 0 && lng === 0) continue;
    latSum += lat;
    lngSum += lng;
    n += 1;
  }
  if (n === 0) return null;
  return { lat: latSum / n, lng: lngSum / n };
}

/**
 * Sunset for the *local solar day* that `now` falls in, at `coord`.
 *
 * Returns null above the polar circles when the sun neither rises nor sets,
 * which is not a Platte County problem but is a one-line guard against
 * `Math.acos` of something outside [-1, 1] producing a NaN clock time.
 *
 * "Local solar day" matters more than it sounds: a canvasser in Missouri at
 * 7pm is already at tomorrow's UTC date. Keying the day number off UTC would
 * hand them tomorrow's sunset and report twenty-five hours of daylight left.
 * So the day number is rounded in local-solar time, which rolls over at solar
 * midnight — the middle of nobody's shift.
 */
export function sunsetAt(coord: Coord, now: Date): Date | null {
  const jd = now.getTime() / 86_400_000 + JD_UNIX_EPOCH;
  const daysFromJ2000 = jd - JD_J2000;

  // Day number, counted in local solar days (see the note above).
  const n = Math.round(daysFromJ2000 + 0.0008 + coord.lng / 360);
  // Mean solar time at this longitude, as a J2000 day offset.
  const jStar = n - coord.lng / 360;

  // Solar mean anomaly.
  const M = (357.5291 + 0.98560028 * jStar) % 360;
  // Equation of the center.
  const C =
    1.9148 * Math.sin(M * DEG) +
    0.02 * Math.sin(2 * M * DEG) +
    0.0003 * Math.sin(3 * M * DEG);
  // Ecliptic longitude.
  const lambda = (M + C + 180 + 102.9372) % 360;

  // Julian date of solar transit (local apparent noon).
  const jTransit =
    JD_J2000 + jStar + 0.0053 * Math.sin(M * DEG) - 0.0069 * Math.sin(2 * lambda * DEG);

  const sinDecl = Math.sin(lambda * DEG) * Math.sin(OBLIQUITY * DEG);
  const cosDecl = Math.cos(Math.asin(sinDecl));

  const cosHourAngle =
    (Math.sin(SUNSET_ALTITUDE * DEG) - Math.sin(coord.lat * DEG) * sinDecl) /
    (Math.cos(coord.lat * DEG) * cosDecl);
  // Midnight sun / polar night: there is no sunset to report.
  if (!Number.isFinite(cosHourAngle) || cosHourAngle > 1 || cosHourAngle < -1) return null;

  const hourAngle = Math.acos(cosHourAngle) / DEG;
  const jSet = jTransit + hourAngle / 360;

  return new Date((jSet - JD_UNIX_EPOCH) * 86_400_000);
}

export interface Daylight {
  sunset: Date;
  /** Whole minutes from `now` to sunset. Negative once the sun is down. */
  minutesLeft: number;
}

/**
 * Sunset plus the minutes remaining, or null when we have no coordinates to
 * compute from. Callers omit the whole element on null rather than showing a
 * placeholder: "—" in a daylight slot reads as "no time left".
 */
export function daylightRemaining(
  points: Array<{ lat?: number | null; lng?: number | null }>,
  now: Date = new Date(),
): Daylight | null {
  const coord = meanCoord(points);
  if (!coord) return null;
  const sunset = sunsetAt(coord, now);
  if (!sunset) return null;
  return {
    sunset,
    minutesLeft: Math.round((sunset.getTime() - now.getTime()) / 60_000),
  };
}

/**
 * "~50 min of light" / "~2 hr 10 min of light" / "after sunset".
 *
 * Rounded to five-minute steps above an hour because minute precision on a
 * two-hour horizon is false precision, and a number that ticks every minute
 * invites watching it instead of the door.
 */
export function formatDaylight(d: Daylight): string {
  const mins = d.minutesLeft;
  if (mins <= 0) return 'after sunset';
  if (mins < 60) return `~${mins} min of light`;
  const rounded = Math.round(mins / 5) * 5;
  const hrs = Math.floor(rounded / 60);
  const rem = rounded % 60;
  return rem === 0 ? `~${hrs} hr of light` : `~${hrs} hr ${rem} min of light`;
}
